import { useMemo } from 'react';
import {
  buildTreeFromNodes,
  extractMermaidSource,
  layoutMermaidGraph,
  looksLikeMermaid,
  normalizeMermaidPayload,
  parseMermaidEdges,
} from '@/lib/scriptAudioAlignVisuals';
import { artifactKind, RENDER_KINDS } from '@/lib/scriptAudioAlignArtifacts';

function num(v, fallback = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

export function TitleSlide({ payload, intro = false }) {
  return (
    <div className="flex flex-col items-center justify-center text-center px-8 py-12 h-full">
      <h2 className={`font-bold text-white mb-3 ${intro ? 'text-4xl tracking-tight' : 'text-3xl'}`}>
        {payload.heading || 'Title'}
      </h2>
      {!intro && payload.body ? (
        <p className="text-lg text-slate-300 max-w-xl">{payload.body}</p>
      ) : null}
    </div>
  );
}

function ExplanationBlock({ payload }) {
  return (
    <div className="px-8 py-6 max-w-2xl mx-auto">
      {payload.heading ? <h3 className="text-xl font-semibold text-violet-200 mb-2">{payload.heading}</h3> : null}
      <p className="text-base text-slate-200 leading-relaxed whitespace-pre-wrap">{payload.body}</p>
    </div>
  );
}

function ExampleCard({ payload }) {
  return (
    <div className="mx-auto max-w-xl rounded-xl border border-amber-500/30 bg-amber-950/30 p-6 m-6">
      <p className="text-xs uppercase tracking-wide text-amber-400 mb-2">Example</p>
      <h3 className="text-lg font-semibold text-white mb-2">{payload.heading || 'Example'}</h3>
      {payload.scenario ? <p className="text-sm text-slate-300 mb-2">{payload.scenario}</p> : null}
      {payload.explain ? <p className="text-sm text-slate-400">{payload.explain}</p> : null}
    </div>
  );
}

function MermaidDiagram({ layout, parsed }) {
  const layers = useMemo(() => {
    const rows = new Map();
    for (const node of layout.nodes || []) {
      const key = String(Math.round(Number(node.y) || 0));
      if (!rows.has(key)) rows.set(key, []);
      rows.get(key).push(node);
    }
    return Array.from(rows.values())
      .map((row) => row.sort((a, b) => a.x - b.x))
      .sort((a, b) => (a[0]?.y || 0) - (b[0]?.y || 0));
  }, [layout.nodes]);

  const edgeLabels = useMemo(() => {
    const labels = parsed?.nodes || {};
    return (parsed?.edges || []).map((edge) => ({
      key: `${edge.from}-${edge.to}`,
      text: `${labels[edge.from] || edge.from} -> ${labels[edge.to] || edge.to}`,
    }));
  }, [parsed]);

  return (
    <div className="space-y-4">
      <div className="space-y-3">
        {layers.map((row, rowIndex) => (
          <div key={`row-${rowIndex}`} className="space-y-3">
            <div className="flex flex-wrap items-center justify-center gap-3">
              {row.map((node) => {
                const label = String(node.label || node.id);
                return (
                  <div
                    key={node.id}
                    className="min-w-[150px] max-w-[220px] rounded-xl border border-cyan-500/40 bg-gradient-to-b from-cyan-950/80 to-slate-900 px-4 py-3 text-center text-sm font-medium text-cyan-50 shadow-[0_0_0_1px_rgba(8,145,178,0.08)]"
                  >
                    {label}
                  </div>
                );
              })}
            </div>
            {rowIndex < layers.length - 1 ? (
              <div className="flex justify-center">
                <div className="flex flex-col items-center text-cyan-300/70">
                  <div className="h-5 w-px bg-cyan-400/40" />
                  <div className="text-xs leading-none">↓</div>
                  <div className="h-5 w-px bg-cyan-400/40" />
                </div>
              </div>
            ) : null}
          </div>
        ))}
      </div>
      {edgeLabels.length ? (
        <div className="rounded-lg border border-slate-800 bg-slate-950/60 px-3 py-2">
          <p className="mb-2 text-[10px] uppercase tracking-wide text-slate-500">Connections</p>
          <div className="flex flex-wrap gap-2">
            {edgeLabels.map((edge) => (
              <span
                key={edge.key}
                className="rounded-full border border-cyan-500/20 bg-cyan-950/30 px-2.5 py-1 text-[11px] text-slate-300"
              >
                {edge.text}
              </span>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function FlowchartView({ payload }) {
  const normalized = useMemo(() => normalizeMermaidPayload(payload), [payload]);
  const source = normalized.mermaid || '';
  const parsed = useMemo(() => parseMermaidEdges(source), [source]);
  const layout = useMemo(() => layoutMermaidGraph(parsed), [parsed]);
  const hasGraph = layout.nodes.length > 0;

  if (!hasGraph) {
    return (
      <div className="p-6">
        {payload.heading ? (
          <h3 className="text-lg font-semibold text-cyan-200 mb-3">{payload.heading}</h3>
        ) : null}
        <pre className="text-xs text-slate-400 whitespace-pre-wrap font-mono rounded-lg border border-slate-700/60 bg-slate-950/60 p-4">
          {source || 'No diagram content'}
        </pre>
      </div>
    );
  }

  return (
    <div className="px-4 py-5 overflow-auto">
      {payload.heading ? (
        <h3 className="text-lg font-semibold text-cyan-200 mb-4 text-center px-4">{payload.heading}</h3>
      ) : null}
      <div className="rounded-xl border border-cyan-500/20 bg-slate-950/40 p-4 shadow-inner">
        <MermaidDiagram layout={layout} parsed={parsed} />
      </div>
    </div>
  );
}

function flattenTreeRows(nodes, depth = 0, rows = []) {
  for (const node of nodes || []) {
    rows.push({ id: node.id, label: node.label, depth });
    if (node.children?.length) flattenTreeRows(node.children, depth + 1, rows);
  }
  return rows;
}

function outlineDepth(line) {
  const raw = String(line).replace(/\t/g, '  ');
  const numbered = raw.match(/^(\s*)(\d+(?:\.\d+)*)(?:[.)]\s+|\s+)(.*\S)\s*$/);
  if (numbered) {
    const nums = numbered[2].split('.');
    return { depth: Math.max(0, nums.length - 1), label: numbered[3].trim() };
  }
  const indent = raw.length - raw.trimStart().length;
  const body = raw.trimStart();
  const bullet = body.match(/^([-*•]+)\s+(.*\S)\s*$/);
  if (bullet) return { depth: Math.floor(indent / 2), label: bullet[2].trim() };
  return { depth: Math.floor(indent / 2), label: body.trim() };
}

function parseOutlineToFlatNodes(content, heading) {
  const lines = String(content || '')
    .split('\n')
    .map((line) => line.replace(/\r$/, ''))
    .filter((line) => line.trim());
  if (!lines.length) return null;
  const hasNesting = lines.some(
    (line) => /^\s{2,}\S/.test(line) || /^\d+\.\d+/.test(line.trim()) || /^\s+[-*•]/.test(line),
  );
  if (!hasNesting) return null;

  const nodes = [];
  if (heading) nodes.push({ id: 'root', label: heading, parent_id: null });
  const stack = [];
  lines.forEach((line, i) => {
    const { depth, label } = outlineDepth(line);
    if (!label) return;
    if (heading && !stack.length && label.toLowerCase() === heading.toLowerCase()) return;
    const nodeId = `n${i}`;
    while (stack.length && stack[stack.length - 1].depth >= depth) stack.pop();
    const parentId = stack.length ? stack[stack.length - 1].id : (heading ? 'root' : null);
    nodes.push({ id: nodeId, label, parent_id: parentId });
    stack.push({ depth, id: nodeId });
  });
  return nodes.length ? nodes : null;
}

function normalizeTreePayload(payload) {
  const p = payload || {};
  if (Array.isArray(p.nodes) && p.nodes.length) {
    return p;
  }
  const heading = String(p.heading || '').trim();
  const bodyText = p.body || p.content || '';
  const parsed = parseOutlineToFlatNodes(bodyText, heading);
  if (parsed) {
    return { ...p, nodes: parsed };
  }
  const items = p.items?.length ? p.items : (bodyText ? String(bodyText).split('\n').filter(Boolean) : []);
  if (!items.length) return p;
  const nodes = [];
  if (heading) {
    nodes.push({ id: 'root', label: heading, parent_id: null });
  }
  items.forEach((item, i) => {
    const label = typeof item === 'string' ? item : String(item?.text || item?.label || item);
    nodes.push({
      id: `n${i}`,
      label: label.trim(),
      parent_id: heading ? 'root' : null,
    });
  });
  return { ...p, nodes };
}

function HierarchyTree({ payload }) {
  const normalized = useMemo(() => normalizeTreePayload(payload), [payload]);
  const roots = useMemo(() => buildTreeFromNodes(normalized.nodes), [normalized.nodes]);
  const rows = useMemo(() => flattenTreeRows(roots), [roots]);
  return (
    <div className="p-6 max-w-2xl mx-auto overflow-auto max-h-[420px]">
      {payload.heading && !rows.some((r) => r.depth === 0 && r.label === payload.heading) ? (
        <h3 className="text-lg font-semibold text-emerald-200 mb-3">{payload.heading}</h3>
      ) : null}
      <ul className="space-y-2 relative">
        {rows.map((row) => (
          <li
            key={row.id}
            className="relative text-sm text-slate-200"
            style={{ marginLeft: `${row.depth * 24}px` }}
          >
            {row.depth > 0 ? (
              <span
                className="absolute -left-3 top-1/2 w-3 border-t border-emerald-500/40"
                aria-hidden
              />
            ) : null}
            <span
              className={[
                'inline-block rounded-lg border px-3 py-1.5',
                row.depth === 0
                  ? 'border-emerald-500/50 bg-emerald-950/40 font-semibold text-emerald-200'
                  : 'border-slate-600/60 bg-slate-900/50',
              ].join(' ')}
            >
              {row.label}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ImageSlide({ payload }) {
  return (
    <div className="flex flex-col items-center justify-center p-8 h-full">
      {payload.heading ? <h3 className="text-xl font-semibold text-white mb-4">{payload.heading}</h3> : null}
      {payload.image_url ? (
        <img src={payload.image_url} alt={payload.heading || 'Visual'} className="max-h-64 rounded-lg object-contain" />
      ) : (
        <div className="w-full max-w-md aspect-video rounded-xl border-2 border-dashed border-slate-600 flex items-center justify-center bg-slate-900/50 p-4">
          <p className="text-sm text-slate-400 text-center">{payload.image_prompt || 'Image placeholder'}</p>
        </div>
      )}
    </div>
  );
}

function SectionHeading({ children, color = 'text-violet-200' }) {
  if (!children) return null;
  return <h3 className={`text-lg font-semibold mb-3 ${color}`}>{children}</h3>;
}

function BulletList({ payload }) {
  const items = payload.items?.length ? payload.items : (payload.body ? [payload.body] : []);
  return (
    <div className="px-8 py-6 max-w-2xl mx-auto">
      <SectionHeading>{payload.heading}</SectionHeading>
      <ul className="space-y-2">
        {items.map((item, i) => (
          <li key={i} className="flex gap-2 text-base text-slate-200 leading-relaxed">
            <span className="text-violet-400 mt-0.5">•</span>
            <span className="whitespace-pre-wrap">{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function StepsList({ payload }) {
  const steps = payload.steps || [];
  return (
    <div className="px-8 py-6 max-w-2xl mx-auto">
      <SectionHeading color="text-cyan-200">{payload.heading}</SectionHeading>
      <ol className="space-y-3">
        {steps.map((step, i) => (
          <li key={i} className="flex gap-3 text-base text-slate-200 leading-relaxed">
            <span className="shrink-0 w-6 h-6 rounded-full bg-cyan-500/20 text-cyan-200 text-xs flex items-center justify-center font-semibold">
              {step.n ?? i + 1}
            </span>
            <span className="whitespace-pre-wrap">{step.text}</span>
          </li>
        ))}
      </ol>
    </div>
  );
}

function normalizeTablePayload(payload) {
  const p = payload || {};
  let columns = Array.isArray(p.columns) ? p.columns.map((c) => String(c)) : [];
  let rows = Array.isArray(p.rows) ? p.rows : [];

  // Recover from stringified / nested payload dumps.
  if ((!columns.length || !rows.length) && (p.body || p.content)) {
    const raw = String(p.body || p.content).trim();
    try {
      const parsed = JSON.parse(raw.replace(/'/g, '"'));
      if (parsed && typeof parsed === 'object') {
        if (Array.isArray(parsed.columns)) columns = parsed.columns.map((c) => String(c));
        if (Array.isArray(parsed.rows)) rows = parsed.rows;
      }
    } catch {
      // Markdown / pipe table fallback
      const lines = raw.split('\n').map((ln) => ln.trim()).filter(Boolean)
        .filter((ln) => !/^[-|: ]+$/.test(ln));
      const parsedRows = lines.map((ln) => {
        const stripped = ln.replace(/^\|/, '').replace(/\|$/, '');
        return stripped.includes('|')
          ? stripped.split('|').map((c) => c.trim())
          : [stripped];
      });
      if (parsedRows.length) {
        columns = parsedRows[0];
        rows = parsedRows.slice(1);
      }
    }
  }

  const normalizedRows = rows.map((row) => {
    if (Array.isArray(row)) return row.map((c) => String(c ?? ''));
    if (row && typeof row === 'object') {
      return columns.length
        ? columns.map((c) => String(row[c] ?? row[c.toLowerCase?.()] ?? ''))
        : [String(row)];
    }
    return [String(row ?? '')];
  });

  return { ...p, columns, rows: normalizedRows };
}

function TableView({ payload }) {
  const normalized = useMemo(() => normalizeTablePayload(payload), [payload]);
  const columns = normalized.columns || [];
  const rows = normalized.rows || [];
  const colCount = Math.max(columns.length, ...rows.map((r) => r.length), 1);

  return (
    <div className="px-5 py-5 overflow-auto h-full">
      <SectionHeading color="text-amber-200">{payload.heading}</SectionHeading>
      {columns.length || rows.length ? (
        <div className="rounded-xl border border-amber-500/25 overflow-hidden bg-slate-950/50 shadow-inner">
          <table className="w-full text-sm border-collapse">
            {columns.length ? (
              <thead>
                <tr className="bg-amber-950/40">
                  {Array.from({ length: colCount }, (_, i) => (
                    <th
                      key={i}
                      className="border-b border-amber-500/20 px-3 py-2.5 text-left text-amber-100 font-semibold"
                    >
                      {columns[i] || ''}
                    </th>
                  ))}
                </tr>
              </thead>
            ) : null}
            <tbody>
              {rows.map((row, ri) => (
                <tr key={ri} className={ri % 2 === 0 ? 'bg-slate-900/30' : 'bg-slate-900/10'}>
                  {Array.from({ length: colCount }, (_, ci) => (
                    <td key={ci} className="border-t border-slate-800/80 px-3 py-2 text-slate-200 align-top leading-snug">
                      {row[ci] || ''}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-sm text-slate-400 whitespace-pre-wrap">
          {payload.body || payload.content || 'No table data'}
        </p>
      )}
    </div>
  );
}

function CodeBlock({ payload }) {
  return (
    <div className="p-6">
      <SectionHeading color="text-emerald-200">{payload.heading}</SectionHeading>
      {payload.language ? (
        <p className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">{payload.language}</p>
      ) : null}
      <pre className="rounded-lg border border-slate-700 bg-slate-900/80 p-4 overflow-auto text-sm text-emerald-100 font-mono whitespace-pre-wrap">
        {payload.code}
      </pre>
    </div>
  );
}

function TimelineView({ payload }) {
  const events = payload.events || [];
  return (
    <div className="px-8 py-6 max-w-2xl mx-auto">
      <SectionHeading color="text-sky-200">{payload.heading}</SectionHeading>
      <ul className="border-l-2 border-sky-500/30 pl-5 space-y-4">
        {events.map((ev, i) => (
          <li key={i} className="relative">
            <span className="absolute -left-[26px] top-1 w-3 h-3 rounded-full bg-sky-400" />
            {ev.time ? <p className="text-xs font-semibold text-sky-300">{ev.time}</p> : null}
            <p className="text-sm text-slate-200 whitespace-pre-wrap">{ev.label}</p>
          </li>
        ))}
      </ul>
    </div>
  );
}

function OriginSection({ title, children, accent = 'amber' }) {
  if (!children) return null;
  const accents = {
    amber: 'border-amber-500/40 text-amber-200',
    rose: 'border-rose-500/40 text-rose-200',
    emerald: 'border-emerald-500/40 text-emerald-200',
    sky: 'border-sky-500/40 text-sky-200',
    violet: 'border-violet-500/40 text-violet-200',
  };
  const tone = accents[accent] || accents.amber;
  return (
    <section className={`rounded-xl border bg-slate-950/40 px-4 py-3 ${tone.split(' ')[0]}`}>
      <p className={`text-[11px] uppercase tracking-[0.14em] font-semibold mb-1.5 ${tone.split(' ')[1]}`}>
        {title}
      </p>
      <div className="text-sm text-slate-200 leading-relaxed whitespace-pre-wrap">{children}</div>
    </section>
  );
}

function OriginView({ payload }) {
  const word = String(payload.word_breakdown || '').trim();
  const meaning = String(payload.meaning || '').trim();
  const naming = String(payload.naming || '').trim();
  const history = String(payload.history || '').trim();
  const body = String(payload.body || '').trim();
  const events = payload.events || [];
  const hasDiagram = Boolean(String(payload.mermaid || '').trim());
  const hasCore = word || meaning || naming || history;

  return (
    <div className="px-5 py-5 max-w-3xl mx-auto space-y-4 overflow-auto">
      <SectionHeading color="text-amber-100">{payload.heading || 'Origin & Background'}</SectionHeading>
      {hasCore ? (
        <div className="grid gap-3 sm:grid-cols-2">
          <OriginSection title="Word breakdown" accent="amber">{word}</OriginSection>
          <OriginSection title="Meaning" accent="rose">{meaning}</OriginSection>
          <OriginSection title="Naming" accent="emerald">{naming}</OriginSection>
          <OriginSection title="History / origin" accent="sky">{history}</OriginSection>
        </div>
      ) : body ? (
        <p className="text-sm text-slate-200 whitespace-pre-wrap leading-relaxed">{body}</p>
      ) : null}
      {events.length ? (
        <div>
          <p className="text-[11px] uppercase tracking-[0.14em] font-semibold text-sky-300 mb-2">
            Evolution
          </p>
          <TimelineView payload={{ events }} />
        </div>
      ) : null}
      {hasDiagram ? (
        <div>
          <p className="text-[11px] uppercase tracking-[0.14em] font-semibold text-cyan-300 mb-2 px-1">
            Evolution diagram
          </p>
          <FlowchartView payload={{ mermaid: payload.mermaid, heading: '' }} />
        </div>
      ) : null}
    </div>
  );
}

function ChartView({ payload }) {
  const points = payload.points || [];
  const max = Math.max(1, ...points.map((p) => num(p.value)));
  return (
    <div className="px-8 py-6 max-w-2xl mx-auto">
      <SectionHeading color="text-fuchsia-200">{payload.heading}</SectionHeading>
      {points.length ? (
        <div className="space-y-2">
          {points.map((p, i) => (
            <div key={i} className="flex items-center gap-3">
              <span className="w-28 shrink-0 text-xs text-slate-300 text-right truncate">{p.label}</span>
              <div className="flex-1 h-4 rounded bg-slate-800 overflow-hidden">
                <div className="h-full bg-fuchsia-500/70" style={{ width: `${(num(p.value) / max) * 100}%` }} />
              </div>
              <span className="w-12 text-xs text-slate-400">{p.value}</span>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-slate-300 whitespace-pre-wrap">{payload.note}</p>
      )}
    </div>
  );
}

function QuizCard({ payload }) {
  return (
    <div className="mx-auto max-w-xl rounded-xl border border-indigo-500/30 bg-indigo-950/30 p-6 m-6">
      <p className="text-xs uppercase tracking-wide text-indigo-300 mb-2">{payload.heading || 'Question'}</p>
      <p className="text-base text-white mb-3 whitespace-pre-wrap">{payload.question}</p>
      {payload.options?.length ? (
        <ul className="space-y-1 mb-3">
          {payload.options.map((opt, i) => (
            <li key={i} className="text-sm text-slate-200">
              <span className="text-indigo-300 mr-2">{String.fromCharCode(65 + i)}.</span>
              {opt}
            </li>
          ))}
        </ul>
      ) : null}
      {payload.answer ? <p className="text-sm text-emerald-300">Answer: {payload.answer}</p> : null}
      {payload.explanation ? <p className="text-xs text-slate-400 mt-1">{payload.explanation}</p> : null}
    </div>
  );
}

function FaqView({ payload }) {
  const qa = payload.qa || [];
  return (
    <div className="px-8 py-6 max-w-2xl mx-auto space-y-3">
      <SectionHeading>{payload.heading}</SectionHeading>
      {qa.map((item, i) => (
        <div key={i}>
          <p className="text-sm font-semibold text-violet-200">Q: {item.q}</p>
          {item.a ? <p className="text-sm text-slate-300 whitespace-pre-wrap">A: {item.a}</p> : null}
        </div>
      ))}
    </div>
  );
}

function StateView({ payload }) {
  const entries = payload.entries || [];
  return (
    <div className="px-8 py-6 max-w-lg mx-auto">
      <SectionHeading color="text-teal-200">{payload.heading}</SectionHeading>
      <div className="rounded-lg border border-teal-500/30 divide-y divide-slate-800">
        {entries.map((e, i) => (
          <div key={i} className="flex justify-between gap-4 px-4 py-2 text-sm">
            <span className="text-teal-300 font-mono">{e.key}</span>
            <span className="text-slate-200 font-mono whitespace-pre-wrap">{e.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export const KIND_RENDERERS = {
  title: TitleSlide,
  prose: ExplanationBlock,
  list: BulletList,
  callout: BulletList,
  steps: StepsList,
  mermaid: FlowchartView,
  tree: HierarchyTree,
  table: TableView,
  code: CodeBlock,
  timeline: TimelineView,
  chart: ChartView,
  example: ExampleCard,
  quiz: QuizCard,
  map: ImageSlide,
  image: ImageSlide,
  faq: FaqView,
  state: StateView,
  origin: OriginView,
};

const LEGACY_TYPE_TO_KIND = {
  title: 'title',
  explanation: 'prose',
  example: 'example',
  flowchart: 'mermaid',
  hierarchy_tree: 'tree',
  concept_breakdown: 'tree',
  concept_tree: 'tree',
  mind_map: 'tree',
  image: 'image',
};

export function resolveVisualKind(element) {
  const payload = element?.payload || {};
  const vtype = String(element?.visual_type || payload.visual_type || '').toLowerCase();
  const catalogKind = artifactKind(vtype);

  if (catalogKind === RENDER_KINDS.MERMAID) return 'mermaid';
  if (catalogKind === RENDER_KINDS.TABLE) return 'table';
  if (catalogKind === RENDER_KINDS.ORIGIN) return 'origin';
  if (catalogKind === RENDER_KINDS.TREE && payload.kind === 'list') return 'tree';

  const fromPayload = payload.kind || LEGACY_TYPE_TO_KIND[vtype] || catalogKind || 'prose';
  const source = extractMermaidSource(payload);
  if (fromPayload === 'prose' && looksLikeMermaid(source)) return 'mermaid';
  return fromPayload;
}

export function VisualLayer({ element }) {
  const payload = element?.payload || {};
  const kind = resolveVisualKind(element);
  const Renderer = KIND_RENDERERS[kind] || ExplanationBlock;
  return <Renderer payload={payload} />;
}
